import { Vorschaubild } from './Vorschaubild';
import type { ApiClient } from '../../api';
import { ZUSTANDSFARBE, farbzustand } from './gemeinsam';
import {
  EmptyState, KpiCard, KpiReihe, PropertyGrid, StatusBadge, type Kennwert,
} from '../../muster';
/**
 * Ein Bauteil einer Kollision — mit allem, womit man es im Modell findet.
 *
 * --- Warum Modellname UND ElementId ---------------------------------------
 *
 * Die RevitElementId gilt je DOKUMENT. Ein Bauteil aus einem verknüpften
 * Modell trägt die Id im fremden Dokument — wer sie im falschen Modell in
 * „Nach ID auswählen" eingibt, landet auf einem beliebigen anderen Bauteil
 * oder auf keinem. Deshalb steht beides beieinander und nie die Id allein.
 *
 * Die UniqueId daneben ist der Weg über die Gewerkeansicht im Add-in: Sie ist
 * modellstabil und funktioniert auch bei nur geladener Verknüpfung.
 */
function Bauteilblock({ b, weicht }: { b: any; weicht: boolean }) {
  if (!b || b.fehlt) {
    return (
      <EmptyState
        klein
        titel="Bauteil nicht auflösbar."
        erklaerung={b?.hinweis ?? undefined}
      />
    );
  }

  /* Modellname UND ElementId — nie die Id allein: Die RevitElementId gilt je
     DOKUMENT. Wer sie im falschen Modell in „Nach ID auswählen" eingibt,
     landet auf einem beliebigen anderen Bauteil oder auf keinem. */
  const eintraege: Kennwert[] = [
    { beschriftung: 'Bauteilart', wert: b.bauteilart },
    { beschriftung: 'System', wert: b.system },
    { beschriftung: 'Geschoss', wert: b.geschoss },
    { beschriftung: 'Abschnitt', wert: b.planungsbereich },
    { beschriftung: 'Modell', wert: b.verknuepfung ?? b.modell, herkunft: 'modell' },
    { beschriftung: 'ElementId', wert: b.elementId, herkunft: 'modell' },
    { beschriftung: 'UniqueId', wert: b.uniqueId, herkunft: 'modell', breit: true },
  ];

  /* Rohmaß und Außenmaß getrennt — der Unterschied ist die Dämmung, und sie
     entscheidet über die Kollision. */
  if (b.durchmesserMm || b.breiteMm || b.daemmungMm) {
    eintraege.push(
      { beschriftung: 'Durchmesser', wert: b.durchmesserMm, einheit: 'mm', nachkomma: 0 },
      {
        beschriftung: 'Breite × Höhe',
        wert: b.breiteMm && b.hoeheMm
          ? `${Math.round(b.breiteMm)} × ${Math.round(b.hoeheMm)} mm`
          : null,
      },
      { beschriftung: 'Dämmung', wert: b.daemmungMm, einheit: 'mm', nachkomma: 0 },
      {
        beschriftung: 'Außenmaß',
        wert: b.aussenBreiteMm && b.aussenHoeheMm
          ? `${Math.round(b.aussenBreiteMm)} × ${Math.round(b.aussenHoeheMm)} mm`
          : null,
      },
      {
        beschriftung: 'Montagezuschlag', wert: b.montagezuschlagMm,
        einheit: 'mm', nachkomma: 0,
      },
    );
  }

  return (
    <div style={{ flex: 1, minWidth: 260 }}>
      <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>
        {b.gewerk} — {b.bezeichnung}
        {weicht && (
          <> <StatusBadge zustand="gut" text="weicht aus" klein /></>
        )}
      </div>

      <PropertyGrid spalten={1} dicht eintraege={eintraege} />
    </div>
  );
}

/**
 * Das Detail einer Kollision: die Anweisung aus der Lösungsableitung, beide
 * Bauteile, die Zahlen.
 *
 * --- Was hier oben steht ---------------------------------------------------
 *
 * Die Anweisung und nicht die Zahlenreihe. Ausdrücklich verlangt, und die
 * Forderung ist richtig: „seitlich_x 569" sagt einem Koordinator nicht, was
 * er tun soll, und „keine_loesung" sagt nicht, warum es keine gibt.
 *
 * Der Text kommt vom Server (Loesungsableitung). Er ist die Bedeutung der
 * Zahlen daneben, und wer ihn hier baute, baute ihn zweimal — einmal für die
 * Seite und einmal für den Bericht.
 */
export function Detailkarte({ d, api, projektId }: { d: any; api?: ApiClient; projektId?: string }) {
  const k = d.kollision ?? {};
  const v = d.vorschlag;
  const band = d.hoehenband;
  const an = d.anweisung;

  // Der Wert wird ZWEIMAL gebraucht: als linke Kante dieser Karte (direkt
  // gerendert) und als Schlüssel für `farbzustand()` daneben. Bis 19.09.2026
  // standen hier Hexwerte - die Kante zeigte danach das alte #b91c1c, während
  // die Marke daneben schon das ausgewichene Burgundy trug. Zwei Rottöne für
  // dieselbe Aussage, und das hellere lag zur Marke bei ΔE00 = 3,24.
  const anFarbe = an?.status === 'angenommen' ? ZUSTANDSFARBE.gut
                : an?.status === 'planer' ? ZUSTANDSFARBE.warnung
                : an?.status === 'abgelehnt' ? ZUSTANDSFARBE.fehler
                : an?.status === 'umgesetzt' ? ZUSTANDSFARBE.gut
                : ZUSTANDSFARBE.neutral;

  return (
    <div style={{
      marginTop: 10,
      border: '1px solid var(--linie)',
      borderLeft: '4px solid ' + anFarbe,
      borderRadius: 4,
      padding: 12,
      background: 'var(--flaeche, #fafafa)',
    }}>
      {/* --- Die Anweisung aus der Lösungsableitung --------------------- */}
      {an ? (
        <>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            {an.titel}
            {an.status && an.status !== 'vorgeschlagen' && (
              <> <StatusBadge
                klein zustand={farbzustand(anFarbe)} text={an.status}
              /></>
            )}
          </div>

          {an.text && (
            <div style={{ fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>
              {an.text}
            </div>
          )}

          {Array.isArray(an.schritte) && an.schritte.length > 0 && (
            <ol style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12 }}>
              {an.schritte.map((s: string, i: number) => <li key={i}>{s}</li>)}
            </ol>
          )}

          {Array.isArray(an.pruefpunkte) && an.pruefpunkte.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 11,
                         color: '#666' }}>
              {an.pruefpunkte.map((p: string, i: number) => <li key={i}>{p}</li>)}
            </ul>
          )}

          <div style={{ fontSize: 11, color: '#666', marginTop: 8 }}>
            Zuständig: {an.zustaendig} · {an.loesungsart?.bezeichnung
              ?? an.loesungsart?.schluessel ?? '—'} · Aufwand {an.aufwand} ·
            Sicherheit {an.sicherheit}
            {an.automatisch && ' · automatisch übernommen'}
          </div>
        </>
      ) : (
        <EmptyState
          klein
          titel="Noch keine Anweisung."
          erklaerung={'Die Lösungsableitung ist für diesen Stand nicht gelaufen, oder '
            + 'die Kollision hat keine Gleichrangregel gefunden (bleibt „nicht '
            + 'geprüft").'}
        />
      )}

      {/* --- Das Vorschaubild: Grundriss und Schnitt, Ist und Soll ------ */}
      {api && projektId && k.id && (
        <Vorschaubild api={api} projektId={projektId} kollisionId={k.id} />
      )}

      {/* --- Die beiden Bauteile --------------------------------------- */}
      <div style={{ display: 'flex', gap: 10, marginTop: 12,
                    flexWrap: 'wrap' }}>
        <Bauteilblock b={d.bauteilA} weicht={d.weichtAus === 'A'} />
        <Bauteilblock b={d.bauteilB} weicht={d.weichtAus === 'B'} />
      </div>

      {/* --- Die Zahlen ------------------------------------------------- */}
      <KpiReihe>
        <KpiCard
          titel="Durchdringung" wert={k.durchdringungMm} einheit="mm" nachkomma={0}
          vergleich={d.loesung?.achse ? `in ${d.loesung.achse}` : undefined}
        />
        <KpiCard
          titel="Überlappung X / Y / Z"
          wert={[k.ueberlappXMm, k.ueberlappYMm, k.ueberlappZMm]
            .map((x: any) => (x ? Math.round(x) : 0)).join(' / ')}
          einheit="mm"
        />
        <KpiCard
          titel="Mitte (Projektsystem)"
          wert={[k.mitteXMm, k.mitteYMm, k.mitteZMm]
            .map((x: any) => (x ? Math.round(x) : 0)).join(' / ')}
          einheit="mm"
        />
        {band?.bandHoeheMm && (
          <KpiCard titel="Höhenband hier" wert={band.bandHoeheMm}
                   einheit="mm" nachkomma={0} />
        )}
        {v?.mitzieher > 0 && (
          <KpiCard
            titel="Mitzuziehen" wert={v.mitzieher} vergleich="Bauteile"
            zustand="warnung" zustandText="hängen an der Bewegung"
          />
        )}
      </KpiReihe>

      {/* --- Was schon entschieden war ---------------------------------- */}
      {(k.istWiedervorlage || k.ablehngrund) && (
        <div className="hinweis" style={{ marginTop: 10, fontSize: 11 }}>
          {k.istWiedervorlage && (
            <div><strong>Wiedervorlage.</strong> Das war entschieden und ist
              seither tiefer geworden.</div>
          )}
          {k.ablehngrund && (
            <div>
              Damals abgelehnt{k.abgelehntVon ? ` von ${k.abgelehntVon}` : ''}
              {k.abgelehntAm
                ? ` am ${new Date(k.abgelehntAm).toLocaleDateString('de-DE')}`
                : ''}: {k.ablehngrund}
            </div>
          )}
        </div>
      )}

      {d.hinweis && (
        <div className="hinweis" style={{ marginTop: 8, fontSize: 11 }}>
          {d.hinweis}
        </div>
      )}
    </div>
  );
}
